import React, { useState, useCallback } from 'react';

import { Button } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';

import { EikaModal } from '../../../components/EikaModal';
import { ErrorCardWrapper } from '../../../components/shared/ErrorCardWrapper';
import AccountList from '../../../components/transaction/AccountList';
import { useAccounts } from '../../../fetchHooks/useAccount';
import { useUpdateLoanDebitAccountNumber } from '../../../fetchHooks/useLoans';
import type { AccountId, AccountNumber } from '../../../generated-types';
import useTranslation from '../../../hooks/useTranslation';
import { isOwnAccount, isValidAccountPaymentFrom } from '../../../services/accountFilters';

type Props = {
    accountId: AccountId;
    show: boolean;
    setShow: React.Dispatch<boolean>;
    currentAccount: AccountNumber;
    onSubmit: () => void;
};

const EditLoanDebitAccountModal: React.FC<Props> = ({ accountId, setShow, currentAccount, show, onSubmit }) => {
    const {
        data: accounts,
        error,
        refetch,
    } = useAccounts({
        select: (data) =>
            data.filter(
                (account) =>
                    account.accountNumber.value !== currentAccount.value &&
                    isValidAccountPaymentFrom(account) &&
                    isOwnAccount(account),
            ),
    });
    const translated = useTranslation();

    const [newDebitAccount, setNewDebitAccount] = useState<AccountNumber | undefined>(currentAccount);

    const updateDebitAccount = useUpdateLoanDebitAccountNumber();

    const closeModal = useCallback(() => {
        if (updateDebitAccount.isSuccess) {
            onSubmit();
        }
        updateDebitAccount.reset();
    }, [updateDebitAccount, onSubmit]);

    const submitOrRetry = useCallback(
        async (debitAccount?: AccountNumber) => {
            if (currentAccount.value === debitAccount?.value || !debitAccount?.value) {
                return;
            }

            await updateDebitAccount.mutateAsync({ accountId, newAccountNumber: debitAccount });
        },
        [currentAccount, updateDebitAccount, accountId],
    );

    const submit = useCallback(
        async (debitAccount: AccountNumber) => {
            setNewDebitAccount(debitAccount);
            await submitOrRetry(debitAccount);
        },
        [submitOrRetry],
    );

    const getStatus = () => {
        if (updateDebitAccount.isSuccess) {
            return 'success';
        }
        if (updateDebitAccount.isPending) {
            return 'loading';
        }

        return null;
    };

    return (
        <EikaModal
            id="edit-loan-debit-account"
            open={show}
            setOpen={setShow}
            onClose={closeModal}
            title={translated.loanEdit.editDebitAccount}
            description={translated.loanEdit.chooseDebitAccount}
            timeOut={updateDebitAccount.isSuccess ? 2500 : undefined}
            contentShouldStretch
        >
            {updateDebitAccount.error && (
                <ErrorCardWrapper
                    isModal
                    defaultError={translated.loanEdit.errors.debitAccountDefaultError}
                    exception={updateDebitAccount.error}
                >
                    <div className="edit-loan__modal-error-actions">
                        <Button onClick={updateDebitAccount.reset} buttonType="link">
                            {translated.cancel}
                        </Button>
                        <ButtonWithSpinner
                            showSpinner={updateDebitAccount.isPending}
                            buttonType="positive"
                            onClick={() => submitOrRetry(newDebitAccount)}
                        >
                            {translated.tryAgain}
                        </ButtonWithSpinner>
                    </div>
                </ErrorCardWrapper>
            )}
            {!updateDebitAccount.error && (
                <form className="edit-loan__modal-form">
                    <AccountList
                        error={error ?? undefined}
                        retry={refetch}
                        accounts={accounts ?? []}
                        selectedAccount={
                            newDebitAccount?.value
                                ? {
                                      number: newDebitAccount,
                                      status: getStatus(),
                                  }
                                : undefined
                        }
                        onClick={submit}
                    />
                </form>
            )}
        </EikaModal>
    );
};

export default EditLoanDebitAccountModal;
